import type { ComponentType, FC } from 'react';
import { useCallback, useState } from 'react';
import { Card, Stack, styled, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { FormikProps } from 'formik';
import { getIn } from 'formik';
import {
  Button,
  CountrySelector,
  FTextInput,
  IbanInput,
  Label,
  Modal,
  ModalInner,
} from '@pflegenavi/web-components';
import { DirectDebitConfirmRedirectWarning } from './DirectDebitConfirmRedirectWarning';
import { PaymentInitiationInfo } from '../PaymentInitiationInfo';
import type { ISetupMangopayDirectDebitValues } from './SetupMangopayDirectDebit';

export interface SetupMangopayDirectDebitViewProps {
  formik: FormikProps<ISetupMangopayDirectDebitValues>;
  disabled: boolean;
  mandatePending: boolean;
  mandateDocumentUrl?: string;
  onCancelMandate: () => void;
  countryOptions: Array<{ label: string; value: string }>;
  onChangeCountry: (newCountryId: string) => void;
  onCountryBlur: () => void;
  onInitiateSubmit: () => Promise<boolean>;
  redirectWarningOpen: boolean;
  setRedirectWarningOpen: (open: boolean) => void;
  PaymentInitiationInfoComponent?: ComponentType;
}

export const SetupMangopayDirectDebitView: FC<
  SetupMangopayDirectDebitViewProps
> = ({
  formik,
  disabled,
  mandatePending,
  mandateDocumentUrl,
  onCancelMandate,
  countryOptions,
  onChangeCountry,
  onCountryBlur,
  onInitiateSubmit,
  redirectWarningOpen,
  setRedirectWarningOpen,
  PaymentInitiationInfoComponent = PaymentInitiationInfo,
}) => {
  const { t } = useTranslation();
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const handleContinueToMangoPay = useCallback(() => {
    if (mandateDocumentUrl) {
      window.open(mandateDocumentUrl, '_blank', 'noopener,noreferrer');
    }
  }, [mandateDocumentUrl]);

  const handleCancelConfirm = useCallback(() => {
    setShowCancelConfirm(false);
    onCancelMandate();
  }, [onCancelMandate]);

  const isDisabled = disabled || mandatePending;

  return (
    <>
      <RootContainer>
        <Stack
          direction="column"
          sx={{
            gap: 3,
          }}
        >
          {mandatePending ? (
            <>
              <Stack
                direction="row"
                sx={{
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Typography variant="h5">
                  {t('mobile.family-member.direct-debit-status')}:
                </Typography>
                <Label color="warning" variant="filled">
                  {t('direct-debit.status.pending-confirmation')}
                </Label>
              </Stack>
              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {t(
                  'mobile.family-member.direct-debit-pending-confirmation-message'
                )}
              </Typography>
            </>
          ) : (
            <Stack
              sx={{
                gap: 1,
              }}
            >
              <Typography variant="h5">
                {t('mobile.family-member.direct-debit-form-title')}
              </Typography>
              <PaymentInitiationInfoComponent />
            </Stack>
          )}
          <Stack
            direction="row"
            sx={{
              gap: 2,
            }}
          >
            <FTextInput
              id="firstName"
              name="firstName"
              label={t('mangopay.direct-debit.setup.first-name')}
              placeholder={t('mangopay.direct-debit.setup.first-name')}
              fullWidth
              formik={formik}
              disabled={isDisabled}
              slotProps={{
                inputLabel: { shrink: true },
              }}
            />
            <FTextInput
              id="lastName"
              name="lastName"
              label={t('mangopay.direct-debit.setup.last-name')}
              placeholder={t('mangopay.direct-debit.setup.last-name')}
              fullWidth
              formik={formik}
              disabled={isDisabled}
              slotProps={{
                inputLabel: { shrink: true },
              }}
            />
          </Stack>
          <IbanInput
            value={formik.values.iban ?? ''}
            setValue={(newIban) => formik.setFieldValue('iban', newIban)}
            error={
              getIn(formik.touched, 'iban') && getIn(formik.errors, 'iban')
            }
            onBlur={() => formik.setFieldTouched('iban', true)}
            disabled={isDisabled}
          />
          <Stack
            direction="row"
            sx={{
              gap: 2,
            }}
          >
            <FTextInput
              id="city"
              name="city"
              label={t('mangopay.direct-debit.setup.city')}
              placeholder={t('mangopay.direct-debit.setup.city')}
              fullWidth
              formik={formik}
              disabled={isDisabled}
              slotProps={{
                inputLabel: { shrink: true },
              }}
            />
          </Stack>
          <Stack
            direction="row"
            sx={{
              gap: 2,
            }}
          >
            <FTextInput
              id="addressLine1"
              name="addressLine1"
              label={t('mangopay.direct-debit.setup.address-line-1')}
              placeholder={t('mangopay.direct-debit.setup.address-line-1')}
              fullWidth
              formik={formik}
              disabled={isDisabled}
              slotProps={{
                inputLabel: { shrink: true },
              }}
            />
            <FTextInput
              id="addressLine2"
              name="addressLine2"
              label={t('mangopay.direct-debit.setup.address-line-2')}
              placeholder={t('mangopay.direct-debit.setup.address-line-2')}
              fullWidth
              formik={formik}
              disabled={isDisabled}
              slotProps={{
                inputLabel: { shrink: true },
              }}
            />
          </Stack>
          <Stack
            direction="row"
            sx={{
              gap: 2,
            }}
          >
            <CountrySelector
              name="country"
              label={t('mangopay.direct-debit.setup.country')}
              onChangeCountry={onChangeCountry}
              onBlur={onCountryBlur}
              countries={countryOptions}
              countryId={formik.values.country}
              errorMessage={
                getIn(formik.touched, 'country') &&
                getIn(formik.errors, 'country')
              }
              fullWidth
              disabled={isDisabled}
            />
            <FTextInput
              id="postalCode"
              name="postalCode"
              label={t('mangopay.direct-debit.setup.postal-code')}
              placeholder={t('mangopay.direct-debit.setup.postal-code')}
              fullWidth
              formik={formik}
              disabled={isDisabled}
              slotProps={{
                inputLabel: { shrink: true },
              }}
            />
          </Stack>
        </Stack>
        {mandatePending ? (
          <Stack
            direction="row"
            sx={{
              gap: 2,
              justifyContent: 'flex-end',
              marginTop: 3,
            }}
          >
            <Button
              variant="outlined"
              color="error"
              onClick={() => setShowCancelConfirm(true)}
            >
              {t('direct-debit.cancel-and-start-over')}
            </Button>
            <Button
              variant="contained"
              onClick={handleContinueToMangoPay}
              disabled={!mandateDocumentUrl}
            >
              {t('mobile.family-member.continue-to-mangopay')}
            </Button>
          </Stack>
        ) : (
          <Stack
            sx={{
              alignItems: 'flex-end',
              marginTop: 3,
            }}
          >
            <Button
              type="submit"
              disabled={disabled || redirectWarningOpen}
              onClick={async () => {
                const isValid = await onInitiateSubmit();
                if (isValid) {
                  setRedirectWarningOpen(true);
                }
              }}
              id="activate-direct-debit-button"
              variant="contained"
            >
              {t('mangopay.direct-debit.setup.initiate')}
            </Button>
          </Stack>
        )}
      </RootContainer>
      <DirectDebitConfirmRedirectWarning
        open={redirectWarningOpen}
        setOpen={setRedirectWarningOpen}
        handleContinue={formik.submitForm}
        submitting={formik.isSubmitting}
      />
      <Modal
        open={showCancelConfirm}
        handleClose={() => setShowCancelConfirm(false)}
      >
        <ModalInner>
          <CancelConfirmModalContainer>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
              }}
            >
              {t('mobile.family-member.warn-modal')}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
                my: 2,
              }}
            >
              {t(
                'mobile.family-member.direct-debit-cancel-pending-confirmation'
              )}
            </Typography>
            <Stack
              direction="row"
              sx={{
                justifyContent: 'flex-end',
                gap: 2,
              }}
            >
              <Button
                variant="outlined"
                onClick={() => setShowCancelConfirm(false)}
              >
                {t('direct-debit.I-do-not-want-to-cancel')}
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleCancelConfirm}
              >
                {t('direct-debit.cancel-and-start-over')}
              </Button>
            </Stack>
          </CancelConfirmModalContainer>
        </ModalInner>
      </Modal>
    </>
  );
};

const RootContainer = styled(Card)(({ theme }) =>
  theme.unstable_sx({
    p: 3,
    maxWidth: 800,
  })
);

const CancelConfirmModalContainer = styled(Stack)(({ theme }) =>
  theme.unstable_sx({
    width: 500,
    maxWidth: '100%',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 3,
    borderRadius: 2,
  })
);
